import { Outlet, useNavigate } from "react-router-dom";
import { AppSidebar } from "./AppSidebar";
import { TopBar } from "./TopBar";
import { WorkspaceProvider } from "@/hooks/use-workspace";
import { useAuth } from "@/hooks/use-auth";
import { UserCheck, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

interface AppLayoutProps {
  title?: string;
}

function ImpersonationBanner() {
  const { impersonatedAs, endImpersonation } = useAuth();
  const navigate = useNavigate();

  if (!impersonatedAs) return null;

  const handleEnd = async () => {
    try {
      await endImpersonation();
      toast.info("Impersonation session ended. Welcome back.");
      navigate("/admin/support", { replace: true });
    } catch {
      toast.error("Failed to restore admin session.");
    }
  };

  return (
    <div className="flex items-center justify-between px-4 py-2 bg-warning/15 border-b border-warning/30 shrink-0">
      <div className="flex items-center gap-2">
        <UserCheck className="w-4 h-4 text-warning shrink-0" />
        <span className="text-sm font-medium text-warning">
          Impersonating:{" "}
          <span className="font-semibold">{impersonatedAs}</span>
          {" — "}all actions are performed as this user
        </span>
      </div>
      <Button
        size="sm"
        variant="outline"
        className="border-warning/40 text-warning hover:bg-warning/10 h-7 text-xs shrink-0"
        onClick={handleEnd}
      >
        <X className="w-3 h-3 mr-1" />
        End Session &amp; Return to Admin
      </Button>
    </div>
  );
}

export function AppLayout({ title }: AppLayoutProps) {
  return (
    <WorkspaceProvider>
      <div className="flex h-screen bg-background">
        <AppSidebar />
        <div className="flex flex-col flex-1 overflow-hidden">
          <ImpersonationBanner />
          <TopBar title={title} />
          <main className="flex-1 overflow-auto p-6">
            <Outlet />
          </main>
        </div>
      </div>
    </WorkspaceProvider>
  );
}
